"use client";

// src/flex-grid.tsx
import * as React2 from "react";
import { Flex } from "@stepstone-genesis/components-flex";
import { systemForwardRef, useTheme } from "@stepstone-genesis/system";

// src/context.tsx
import * as React from "react";
var FlexGridContext = React.createContext(
  null
);
var useFlexGridContext = () => {
  const context = React.useContext(FlexGridContext);
  if (!context) {
    throw new Error("useFlexGridContext must be used within FlexGrid");
  }
  return context;
};

// src/helpers.tsx
import { isArray } from "@stepstone-genesis/utilities";
var calculateWidth = (size, width) => `${size * width}%`;
var calculateWithinArray = (size, width) => size !== null ? calculateWidth(size, width) : null;
var convertThemeValueToValue = (theme, value, key = "space") => {
  return parseFloat(theme[key][value]);
};
var calculateColumnWidth = (totalColumns) => {
  return totalColumns && totalColumns > 2 ? Number(parseFloat((100 / totalColumns).toString())) : 100;
};
var calculateColumnSize = (width, columnSize) => {
  return isArray(columnSize) && columnSize.length > 1 ? columnSize.map(
    (size) => size ? calculateWithinArray(size, width) : null
  ) : calculateWidth(columnSize, width);
};
var calculateColumnOffset = (width, offset) => {
  if (isArray(offset) && offset.length > 1) {
    return offset.map(
      (size) => size ? calculateWithinArray(size, width) : null
    );
  }
  return offset && offset > 0 ? calculateWidth(offset, width) : void 0;
};
var calculateGutterWidth = (theme, gutterWidth, wrapper = false, negative = true) => {
  if (isArray(gutterWidth) && gutterWidth.length > 1) {
    return gutterWidth.map((gutter) => {
      if (gutter === null || !gutter)
        return null;
      const value2 = convertThemeValueToValue(theme, gutter);
      if (value2 > 0 && wrapper)
        return negative ? `-${value2 / 2}px` : `${value2 / 2}px`;
      return `${value2 > 0 ? value2 / 2 : 0}px`;
    });
  }
  const value = convertThemeValueToValue(theme, gutterWidth);
  if (value > 0 && wrapper)
    return negative ? `-${value / 2}px` : `${value / 2}px`;
  return `${value > 0 ? value / 2 : 0}px`;
};
var useFlexGrid = ({
  theme,
  totalColumns = 12,
  offset = 0,
  span = 1,
  verticalGutter = "spaceNone",
  horizontalGutter = "spaceNone"
}) => {
  const columnWidth = calculateColumnWidth(totalColumns);
  const columnOffset = calculateColumnOffset(columnWidth, offset);
  const columnSize = calculateColumnSize(columnWidth, span);
  const verticalGutterSpace = calculateGutterWidth(theme, verticalGutter);
  const horizontalGutterSpace = calculateGutterWidth(theme, horizontalGutter);
  return {
    columnWidth,
    columnOffset,
    columnSize,
    verticalGutterSpace,
    horizontalGutterSpace
  };
};

// src/flex-grid.tsx
var FlexGrid = systemForwardRef(function FlexGrid2({
  children,
  gutter = ["spaceM", "spaceL"],
  horizontalGutter,
  verticalGutter,
  totalColumns = 12,
  element = "FLEX_GRID",
  ...rest
}, ref) {
  const { theme } = useTheme();
  const horizontalOuterGutter = calculateGutterWidth(
    theme,
    horizontalGutter != null ? horizontalGutter : gutter,
    true
  );
  const verticalOuterGutter = calculateGutterWidth(
    theme,
    verticalGutter != null ? verticalGutter : gutter,
    true
  );
  const adjacentGridGutter = calculateGutterWidth(
    theme,
    verticalGutter != null ? verticalGutter : gutter,
    true,
    // we want positive not negative value
    false
  );
  return /* @__PURE__ */ React2.createElement(
    FlexGridContext.Provider,
    {
      value: { gutter, horizontalGutter, verticalGutter, totalColumns, theme }
    },
    /* @__PURE__ */ React2.createElement(
      Flex,
      {
        ref,
        flxDirection: "row",
        flxWrap: "wrap",
        __root: {
          // for any grid in a stack we need to add this otherwise the user has to deal with margins
          "& + &": {
            marginTop: adjacentGridGutter
          }
        },
        marginY: verticalOuterGutter,
        marginX: horizontalOuterGutter,
        "data-genesis-element": element,
        ...rest
      },
      children
    )
  );
});

// src/flex-column.tsx
import * as React3 from "react";
import {
  systemForwardRef as systemForwardRef2
} from "@stepstone-genesis/system";
import { FlexItem } from "@stepstone-genesis/components-flex";
var FlexColumn = systemForwardRef2(function FlexColumn2({ children, gutter, verticalGutter, horizontalGutter, span, offset, ...rest }, ref) {
  var _a, _b;
  const {
    totalColumns,
    theme,
    gutter: ctxGutter,
    horizontalGutter: ctxHorizontalGutter,
    verticalGutter: ctxVerticalGutter
  } = useFlexGridContext();
  const {
    horizontalGutterSpace,
    verticalGutterSpace,
    columnSize,
    columnOffset
  } = useFlexGrid({
    theme,
    totalColumns,
    verticalGutter: (_a = verticalGutter || ctxVerticalGutter) != null ? _a : gutter || ctxGutter,
    horizontalGutter: (_b = horizontalGutter || ctxHorizontalGutter) != null ? _b : gutter || ctxGutter,
    offset,
    span
  });
  return /* @__PURE__ */ React3.createElement(
    FlexItem,
    {
      ref,
      ...!span && {
        flxGrow: 1,
        flxShrink: 1,
        flxBasis: 0
      },
      __root: {
        // For span grids e.g. span={2}
        ...span && {
          width: columnSize
        },
        // For offsets
        marginLeft: columnOffset,
        // gutter widths
        paddingY: verticalGutterSpace,
        paddingX: horizontalGutterSpace
      },
      ...rest
    },
    children
  );
});
export {
  FlexColumn,
  FlexGrid
};
